/**
 * As integrações: as conexões aos bancos dos clientes. Só a Cliqfy vê.
 *
 * - Cada conexão mostra a situação do último teste, o endereço, o banco e o
 *   usuário. A senha fica selada no cofre do serviço e nunca volta à tela.
 * - Criar e editar no painel lateral; editar não muda o tipo de banco, e só
 *   troca a senha quando a pessoa pede.
 * - O teste mostra o resultado etapa por etapa; o mapa do banco mostra as
 *   tabelas e colunas lidas por último, e pode ser lido de novo.
 * - Remover é recusado enquanto uma operação publicada usa a conexão.
 * - Com a clínica ligada, a "Agenda da clínica" liga cada parte da tela da
 *   recepção a uma operação (ADR-0028).
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { chaves } from '@/shared/api/chaves';
import { useApi, useConexoesDeDados, useMapaDoBanco } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import {
  Botao,
  CabecalhoDeTela,
  Campo,
  CampoSelecao,
  Dialogo,
  Icone,
  Selo,
  type NomeDoIcone,
} from '@/shared/ui';
import { Estado, Vazio } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import { useSessao } from '@/shared/sessao/contexto';
import {
  CENARIO,
  SITUACAO_DA_CONEXAO,
  TIPOS_DE_BANCO,
  corpoParaCriar,
  corpoParaEditar,
  formularioDe,
  formularioVazio,
  problemasDoFormulario,
  trocarTipo,
  type Conexao,
  type Familia,
  type Formulario,
} from '../lib/conexoes';
import { AgendaDaClinica } from './agenda-da-clinica';
import './conexoes.css';

const SERVICO_FORA =
  'O serviço das automações não respondeu agora. Nada foi alterado; tente de novo daqui a pouco.';

function quando(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Integracoes({
  nova,
  aoFecharNova,
}: {
  nova: boolean;
  aoFecharNova: () => void;
}): ReactNode {
  const conexoes = useConexoesDeDados();
  const { estado } = useSessao();
  const clinica = estado.nome === 'autenticado' && (estado.me.flags['clinica'] ?? false);
  const [criando, definirCriando] = useState(false);
  const [editando, definirEditando] = useState<Conexao | null>(null);
  const [testando, definirTestando] = useState<Conexao | null>(null);
  const [vendoMapa, definirVendoMapa] = useState<Conexao | null>(null);
  const [removendo, definirRemovendo] = useState<Conexao | null>(null);
  const [recado, definirRecado] = useState<string | null>(null);
  /** A conexão que acabou de nascer: o recado oferece testá-la já. */
  const [paraTestar, definirParaTestar] = useState<Conexao | null>(null);
  const avisar = (texto: string, testar: Conexao | null = null): void => {
    definirRecado(texto);
    definirParaTestar(testar);
  };
  /*
   * Remover tira da tela o cartão e o botão que abriu a confirmação, e o foco
   * cairia no começo da página. Ele vai para o recado, que diz o que houve.
   */
  const recadoRef = useRef<HTMLParagraphElement>(null);
  const [focarNoRecado, definirFocarNoRecado] = useState(false);
  useEffect(() => {
    if (!focarNoRecado) return;
    recadoRef.current?.focus();
    definirFocarNoRecado(false);
  }, [focarNoRecado]);

  const abrirNova = (): void => definirCriando(true);
  const fecharNova = (): void => {
    definirCriando(false);
    if (nova) aoFecharNova();
  };

  return (
    <div className="conexoes">
      <CabecalhoDeTela
        titulo="Integrações"
        retrato={
          <span className="conexoes__icone-do-titulo" aria-hidden="true">
            <Icone nome="api" />
          </span>
        }
        apoio="As conexões aos bancos dos clientes, de onde saem as listas e as operações SQL."
        acoes={
          <>
            <Selo tom="info">Só Cliqfy</Selo>
            <Botao variante="primario" onClick={abrirNova}>
              Nova conexão
            </Botao>
          </>
        }
      />
      <div className="conexoes__conteudo">
        <p className="conexoes__aviso">
          <Icone nome="alerta" />
          <span>
            As senhas ficam seladas no cofre do serviço. Depois de salvas, ninguém as vê de novo,
            nem aqui.
          </span>
        </p>

        <div className="conexoes__recado-linha" data-vazio={recado ? undefined : 'sim'}>
          <p
            className="conexoes__recado"
            role="status"
            aria-live="polite"
            tabIndex={-1}
            ref={recadoRef}
          >
            {recado ?? ''}
          </p>
          {recado && paraTestar && (
            <Botao
              variante="secundario"
              miudo
              onClick={() => {
                definirTestando(paraTestar);
                definirParaTestar(null);
              }}
              aria-label={`Testar ${paraTestar.nome} agora`}
            >
              <Icone nome="check" />
              Testar agora
            </Botao>
          )}
        </div>

        <Estado consulta={conexoes} forma="lista" linhas={3}>
          {(dados) =>
            dados.itens.length === 0 ? (
              <Vazio
                titulo="Nenhuma conexão ainda"
                porque="Uma conexão liga o CRM ao banco de um cliente. Cadastre a primeira e teste antes de escrever as operações."
                acao={{ rotulo: 'Cadastrar a primeira', aoClicar: abrirNova }}
              />
            ) : (
              <ul className="conexoes__lista">
                {dados.itens.map((c) => (
                  <CartaoDaConexao
                    key={c.id}
                    conexao={c}
                    aoEditar={() => definirEditando(c)}
                    aoTestar={() => definirTestando(c)}
                    aoVerMapa={() => definirVendoMapa(c)}
                    aoRemover={() => definirRemovendo(c)}
                  />
                ))}
              </ul>
            )
          }
        </Estado>

        {clinica && <AgendaDaClinica />}

        {(criando || nova) && (
          <FormularioDaConexao
            conexao={null}
            aoFechar={fecharNova}
            aoSalva={(salva) => {
              fecharNova();
              avisar(`${salva.nome} cadastrada. Teste a conexão antes de usar.`, salva);
            }}
          />
        )}
        {editando && (
          <FormularioDaConexao
            conexao={editando}
            aoFechar={() => definirEditando(null)}
            aoSalva={(salva) => {
              definirEditando(null);
              avisar(`${salva.nome} salva. Ela volta a "Nunca testada": teste de novo.`, salva);
            }}
          />
        )}
        {testando && <TesteDaConexao conexao={testando} aoFechar={() => definirTestando(null)} />}
        {vendoMapa && <MapaDaConexao conexao={vendoMapa} aoFechar={() => definirVendoMapa(null)} />}
        {removendo && (
          <RemoverConexao
            conexao={removendo}
            aoFechar={() => definirRemovendo(null)}
            aoRemovida={(nome) => {
              definirRemovendo(null);
              avisar(`${nome} removida.`);
              definirFocarNoRecado(true);
            }}
          />
        )}
      </div>
    </div>
  );
}

const ICONE_DO_TESTE: Record<Conexao['situacao'], NomeDoIcone> = {
  ok: 'check',
  falhou: 'alerta',
  nao_testada: 'relogio',
};

function CartaoDaConexao({
  conexao: c,
  aoEditar,
  aoTestar,
  aoVerMapa,
  aoRemover,
}: {
  conexao: Conexao;
  aoEditar: () => void;
  aoTestar: () => void;
  aoVerMapa: () => void;
  aoRemover: () => void;
}): ReactNode {
  const tipo = TIPOS_DE_BANCO[c.familia];
  const situacao = SITUACAO_DA_CONEXAO[c.situacao];
  return (
    <li className="conexoes__cartao" data-situacao={c.situacao} aria-labelledby={`conexao-${c.id}`}>
      <div className="conexoes__topo">
        <span className="conexoes__sigla" data-familia={c.familia} aria-hidden="true">
          {tipo.sigla}
        </span>
        <div className="conexoes__titulo">
          <h2 id={`conexao-${c.id}`} className="conexoes__nome">
            {c.nome}
          </h2>
          <p className="conexoes__tipo">{tipo.rotulo}</p>
        </div>
        <Selo tom={situacao.tom}>{situacao.rotulo}</Selo>
      </div>

      <dl className="conexoes__dados">
        <div>
          <dt>Endereço</dt>
          <dd>
            <code>
              {c.host}:{c.porta}
            </code>
          </dd>
        </div>
        <div>
          <dt>Banco</dt>
          <dd>
            <code>{c.banco}</code>
          </dd>
        </div>
        <div>
          <dt>Usuário</dt>
          <dd>
            <code>{c.usuario}</code>
          </dd>
        </div>
        <div>
          <dt>Senha</dt>
          <dd>
            <span className="conexoes__selada">
              <span className="conexoes__selada-pontos" aria-hidden="true">
                •••••
              </span>
              selada no cofre
            </span>
          </dd>
        </div>
        <div>
          <dt>Como chega</dt>
          <dd>{CENARIO[c.cenario]}</dd>
        </div>
      </dl>

      <p className="conexoes__ultimo-teste" data-situacao={c.situacao}>
        <Icone nome={ICONE_DO_TESTE[c.situacao]} />
        <span>
          <strong>Último teste:</strong> {c.testada_em ? quando(c.testada_em) : 'Nunca'}
          {c.testada_em &&
            ` · ${c.situacao === 'ok' ? 'conectou' : c.situacao === 'falhou' ? 'falhou' : 'teste de novo'}`}
        </span>
      </p>

      <div className="conexoes__acoes">
        <Botao variante="secundario" miudo onClick={aoTestar} aria-label={`Testar ${c.nome}`}>
          <Icone nome="check" />
          Testar
        </Botao>
        <Botao
          variante="secundario"
          miudo
          onClick={aoVerMapa}
          aria-label={`Mapa do banco de ${c.nome}`}
        >
          <Icone nome="menu" />
          Mapa do banco
        </Botao>
        <Botao variante="fantasma" miudo onClick={aoEditar} aria-label={`Editar ${c.nome}`}>
          <Icone nome="lapis" />
          Editar
        </Botao>
        <Botao variante="fantasma" miudo onClick={aoRemover} aria-label={`Remover ${c.nome}`}>
          <Icone nome="lixeira" />
          Remover
        </Botao>
      </div>
    </li>
  );
}

/** Criar (`conexao` nulo) ou editar uma conexão. */
function FormularioDaConexao({
  conexao,
  aoFechar,
  aoSalva,
}: {
  conexao: Conexao | null;
  aoFechar: () => void;
  aoSalva: (salva: Conexao) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const criando = conexao === null;
  const [chave] = useState(() => crypto.randomUUID());
  const [f, definirF] = useState<Formulario>(() =>
    conexao ? formularioDe(conexao) : formularioVazio(),
  );
  const [trocandoSenha, definirTrocandoSenha] = useState(criando);
  /** Ver o que se digitou: só no formulário, antes de ir ao cofre. */
  const [vendoSenha, definirVendoSenha] = useState(false);
  const [tentou, definirTentou] = useState(false);

  const problemas = problemasDoFormulario(f, trocandoSenha);
  const erro = (campo: keyof Formulario): { erro: string } | Record<string, never> =>
    tentou && problemas[campo] ? { erro: problemas[campo] } : {};
  const mudar = (campo: keyof Formulario) => (valor: string) => definirF({ ...f, [campo]: valor });

  const salvar = useMutation({
    mutationFn: () =>
      conexao
        ? api!.editarConexaoDeDados({
            caminho: { id: conexao.id },
            corpo: corpoParaEditar(f, trocandoSenha),
          })
        : api!.criarConexaoDeDados({ corpo: corpoParaCriar(f), idempotencyKey: chave }),
    onSuccess: (salva) => aoSalva(salva),
    onSettled: () => void cache.invalidateQueries({ queryKey: chaves.conexoesDeDados() }),
  });

  const enviar = (e: FormEvent): void => {
    e.preventDefault();
    definirTentou(true);
    if (Object.keys(problemas).length > 0 || salvar.isPending) return;
    salvar.mutate();
  };

  const idForm = criando ? 'nova-conexao' : `editar-conexao-${conexao.id}`;
  const salvando = criando ? 'Cadastrando…' : 'Salvando…';

  return (
    <Dialogo
      titulo={criando ? 'Nova conexão' : `Editar ${conexao.nome}`}
      forma="lateral"
      aoFechar={() => {
        if (!salvar.isPending) aoFechar();
      }}
      botoes={
        <>
          <Botao
            variante="fantasma"
            onClick={aoFechar}
            desabilitado={salvar.isPending}
            motivo={salvando}
          >
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form={idForm}
            variante="primario"
            desabilitado={salvar.isPending || api === null}
            motivo={salvar.isPending ? salvando : 'Sem sessão: entre de novo.'}
          >
            {salvar.isPending ? salvando : criando ? 'Cadastrar' : 'Salvar'}
          </Botao>
        </>
      }
    >
      <form id={idForm} className="conexoes__form" onSubmit={enviar} noValidate>
        <Campo
          rotulo="Nome da conexão"
          placeholder="Ex.: Sistema da clínica"
          maxLength={120}
          value={f.nome}
          onChange={(e) => mudar('nome')(e.target.value)}
          {...erro('nome')}
        />
        <CampoSelecao
          rotulo="Tipo de banco"
          value={f.familia}
          disabled={!criando}
          onChange={(e) => definirF(trocarTipo(f, e.target.value as Familia))}
          {...(criando ? {} : { ajuda: 'O tipo não muda depois de cadastrada.' })}
        >
          {(Object.keys(TIPOS_DE_BANCO) as Familia[]).map((familia) => (
            <option key={familia} value={familia}>
              {TIPOS_DE_BANCO[familia].rotulo}
            </option>
          ))}
        </CampoSelecao>
        <div className="conexoes__linha">
          <Campo
            rotulo="Endereço do servidor"
            placeholder="db.empresa.com.br"
            maxLength={255}
            autoComplete="off"
            spellCheck={false}
            value={f.host}
            onChange={(e) => mudar('host')(e.target.value)}
            {...erro('host')}
          />
          <Campo
            rotulo="Porta"
            inputMode="numeric"
            maxLength={5}
            value={f.porta}
            onChange={(e) => mudar('porta')(e.target.value)}
            {...erro('porta')}
          />
        </div>
        <Campo
          rotulo="Nome do banco"
          maxLength={128}
          autoComplete="off"
          spellCheck={false}
          value={f.banco}
          onChange={(e) => mudar('banco')(e.target.value)}
          {...erro('banco')}
        />
        <Campo
          rotulo="Usuário"
          ajuda="Prefira um usuário só de leitura."
          maxLength={128}
          autoComplete="off"
          spellCheck={false}
          value={f.usuario}
          onChange={(e) => mudar('usuario')(e.target.value)}
          {...erro('usuario')}
        />
        {trocandoSenha ? (
          <div className="conexoes__senha">
            <Campo
              rotulo={criando ? 'Senha' : 'Senha nova'}
              type={vendoSenha ? 'text' : 'password'}
              spellCheck={false}
              autoComplete="new-password"
              maxLength={512}
              ajuda="Vai direto para o cofre e não volta à tela."
              value={f.senha}
              onChange={(e) => mudar('senha')(e.target.value)}
              {...erro('senha')}
            />
            <Botao
              variante="fantasma"
              miudo
              aria-pressed={vendoSenha}
              onClick={() => definirVendoSenha((v) => !v)}
            >
              <Icone nome="olho" />
              Mostrar a senha
            </Botao>
            {!criando && (
              <Botao
                variante="fantasma"
                miudo
                onClick={() => {
                  definirTrocandoSenha(false);
                  definirF({ ...f, senha: '' });
                }}
              >
                Manter a senha atual
              </Botao>
            )}
          </div>
        ) : (
          <div className="conexoes__senha">
            <p className="conexoes__nota">
              Senha: <span className="conexoes__selada">selada no cofre</span>
            </p>
            <Botao variante="secundario" miudo onClick={() => definirTrocandoSenha(true)}>
              Trocar senha
            </Botao>
          </div>
        )}
        <fieldset className="conexoes__cenario">
          <legend>Como o serviço chega ao banco</legend>
          {(Object.keys(CENARIO) as Conexao['cenario'][]).map((cenario) => (
            <label key={cenario} className="conexoes__opcao">
              <input
                type="radio"
                name={`${idForm}-cenario`}
                value={cenario}
                checked={f.cenario === cenario}
                onChange={() => definirF({ ...f, cenario })}
              />
              <span>{CENARIO[cenario]}</span>
            </label>
          ))}
        </fieldset>
        {!criando && <p className="conexoes__nota">Salvar volta a conexão a “Nunca testada”.</p>}
        {salvar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(salvar.error, {
              generica: 'Não foi possível salvar agora. Nada foi alterado; tente de novo.',
              porCodigo: { ENGINE_INDISPONIVEL: SERVICO_FORA },
            })}
          </Faixa>
        )}
      </form>
    </Dialogo>
  );
}

/** O teste começa ao abrir; o resultado vem etapa por etapa. */
function TesteDaConexao({
  conexao,
  aoFechar,
}: {
  conexao: Conexao;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const testar = useMutation({
    mutationFn: () => api!.testarConexaoDeDados({ caminho: { id: conexao.id } }),
    onSettled: () => void cache.invalidateQueries({ queryKey: chaves.conexoesDeDados() }),
  });
  const comecou = useRef(false);
  useEffect(() => {
    if (comecou.current || api === null) return;
    comecou.current = true;
    testar.mutate();
  }, [api, testar]);

  const r = testar.data;
  return (
    <Dialogo
      titulo={`Testar ${conexao.nome}`}
      largura="estreita"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Fechar
          </Botao>
          <Botao
            variante="primario"
            onClick={() => testar.mutate()}
            desabilitado={testar.isPending || api === null}
            motivo={testar.isPending ? 'Testando…' : 'Sem sessão: entre de novo.'}
          >
            {testar.isPending ? 'Testando…' : 'Testar de novo'}
          </Botao>
        </>
      }
    >
      <div className="conexoes__teste" aria-busy={testar.isPending}>
        {testar.isPending && (
          <p className="conexoes__testando">
            <span className="conexoes__girador" aria-hidden="true" />
            Testando a conexão…
          </p>
        )}
        {testar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(testar.error, {
              generica: 'Não foi possível testar agora. Tente de novo.',
              porCodigo: { ENGINE_INDISPONIVEL: SERVICO_FORA },
            })}
          </Faixa>
        )}
        {r && !testar.isPending && (
          <>
            <p className="conexoes__veredito" data-ok={r.ok ? 'sim' : 'nao'}>
              {r.ok
                ? 'A conexão funcionou. Ela já pode ser usada nas operações.'
                : 'A conexão falhou. Veja em que etapa parou.'}
            </p>
            <ol className="conexoes__etapas">
              {/* As etapas que o serviço devolveu, reveladas em ordem: o resultado
                  já chegou inteiro, e a sequência só mostra o caminho. */}
              {r.etapas.map((etapa, i) => (
                <li
                  key={etapa.nome}
                  className="conexoes__etapa"
                  data-ok={etapa.ok ? 'sim' : 'nao'}
                  style={{ '--i': Math.min(i, 6) } as CSSProperties}
                >
                  <span className="conexoes__etapa-marca" aria-hidden="true">
                    <Icone nome={etapa.ok ? 'check' : 'fechar'} />
                  </span>
                  <span className="conexoes__etapa-nome">
                    {etapa.nome}
                    <span className="visualmente-oculto">
                      {etapa.ok ? ': deu certo' : ': falhou'}
                    </span>
                  </span>
                  {etapa.detalhe && (
                    <span className="conexoes__etapa-detalhe">{etapa.detalhe}</span>
                  )}
                </li>
              ))}
            </ol>
          </>
        )}
      </div>
    </Dialogo>
  );
}

/** As tabelas e colunas lidas por último; "Ler de novo" lê ao vivo. */
function MapaDaConexao({
  conexao,
  aoFechar,
}: {
  conexao: Conexao;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const mapa = useMapaDoBanco(conexao.id);
  const [busca, definirBusca] = useState('');
  const ler = useMutation({
    mutationFn: () => api!.atualizarMapaDoBanco({ caminho: { id: conexao.id } }),
    onSuccess: (novo) => cache.setQueryData(chaves.mapaDoBanco(conexao.id), novo),
  });
  const nuncaLido = mapa.data?.versao === 0;

  return (
    <Dialogo
      titulo={`Mapa de ${conexao.nome}`}
      forma="lateral"
      largura="larga"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Fechar
          </Botao>
          <Botao
            variante="primario"
            onClick={() => ler.mutate()}
            desabilitado={ler.isPending || api === null}
            motivo={ler.isPending ? 'Lendo o banco…' : 'Sem sessão: entre de novo.'}
          >
            {ler.isPending ? 'Lendo o banco…' : nuncaLido ? 'Ler o banco' : 'Ler de novo'}
          </Botao>
        </>
      }
    >
      <div className="conexoes__mapa">
        <p className="conexoes__nota">
          As tabelas e colunas que o usuário da conexão enxerga. O SQL das operações é conferido
          contra este mapa.
        </p>
        {ler.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(ler.error, {
              generica: 'Não foi possível ler o banco agora. O mapa anterior continua valendo.',
              porCodigo: {
                ENGINE_INDISPONIVEL: SERVICO_FORA,
                BANCO_DO_CLIENTE_INDISPONIVEL:
                  'O banco do cliente não respondeu. Teste a conexão para ver em que etapa para.',
              },
            })}
          </Faixa>
        )}
        {ler.data && !ler.isPending && (
          <p className="conexoes__recado" role="status">
            {ler.data.mudou
              ? `O banco mudou desde a última leitura. Agora é a versão ${ler.data.versao}.`
              : 'Nada mudou desde a última leitura.'}
          </p>
        )}
        <Estado consulta={mapa} forma="lista" linhas={4}>
          {(dados) => {
            if (dados.versao === 0) {
              return (
                <Vazio
                  titulo="O banco ainda não foi lido"
                  porque="Leia o banco para ver as tabelas e colunas. É bom testar a conexão antes."
                />
              );
            }
            const termo = busca.trim().toLowerCase();
            const tabelas = termo
              ? dados.tabelas.filter(
                  (t) =>
                    `${t.esquema}.${t.nome}`.toLowerCase().includes(termo) ||
                    t.colunas.some((c) => c.nome.toLowerCase().includes(termo)),
                )
              : dados.tabelas;
            return (
              <>
                <div className="conexoes__ferramentas">
                  <div className="conexoes__busca">
                    <Campo
                      type="search"
                      icone="buscar"
                      rotuloOculto="Buscar tabela ou coluna"
                      placeholder="Buscar tabela ou coluna"
                      value={busca}
                      onChange={(e) => definirBusca(e.target.value)}
                    />
                  </div>
                  <p className="conexoes__nota">
                    Versão {dados.versao} ·{' '}
                    {dados.tabelas.length === 1 ? '1 tabela' : `${dados.tabelas.length} tabelas`}
                  </p>
                </div>
                {tabelas.length === 0 ? (
                  <p className="conexoes__nota">Nenhuma tabela ou coluna com “{busca.trim()}”.</p>
                ) : (
                  <ul className="conexoes__tabelas">
                    {tabelas.map((t) => (
                      <li key={`${t.esquema}.${t.nome}`}>
                        <details className="conexoes__tabela" open={termo !== ''}>
                          <summary>
                            <code>
                              {t.esquema}.{t.nome}
                            </code>
                            <span className="conexoes__nota">
                              {t.colunas.length === 1 ? '1 coluna' : `${t.colunas.length} colunas`}
                            </span>
                          </summary>
                          <ul className="conexoes__colunas">
                            {t.colunas.map((c) => (
                              <li key={c.nome} className="conexoes__coluna">
                                <code>{c.nome}</code>
                                <span className="conexoes__coluna-tipo">
                                  {c.tipo}
                                  {c.tamanho_maximo !== null ? `(${c.tamanho_maximo})` : ''}
                                  {c.aceita_vazio ? ' · aceita vazio' : ''}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            );
          }}
        </Estado>
      </div>
    </Dialogo>
  );
}

function RemoverConexao({
  conexao,
  aoFechar,
  aoRemovida,
}: {
  conexao: Conexao;
  aoFechar: () => void;
  aoRemovida: (nome: string) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const porQue = useId();
  const remover = useMutation({
    mutationFn: () => api!.removerConexaoDeDados({ caminho: { id: conexao.id } }),
    onSuccess: () => {
      // O cartão sai já, junto com o diálogo, sem esperar a lista voltar.
      cache.setQueryData<{ itens: Conexao[] }>(chaves.conexoesDeDados(), (atual) =>
        atual ? { ...atual, itens: atual.itens.filter((c) => c.id !== conexao.id) } : atual,
      );
      aoRemovida(conexao.nome);
    },
    onSettled: () => void cache.invalidateQueries({ queryKey: chaves.conexoesDeDados() }),
  });
  return (
    <Dialogo
      titulo={`Remover ${conexao.nome}?`}
      largura="estreita"
      papel="alertdialog"
      descricaoId={porQue}
      consequencia
      aoFechar={() => {
        if (!remover.isPending) aoFechar();
      }}
      botoes={
        <>
          <Botao
            variante="fantasma"
            onClick={aoFechar}
            desabilitado={remover.isPending}
            motivo="Removendo…"
          >
            Cancelar
          </Botao>
          <Botao
            variante="perigo"
            onClick={() => remover.mutate()}
            desabilitado={remover.isPending || api === null}
            motivo={remover.isPending ? 'Removendo…' : 'Sem sessão: entre de novo.'}
          >
            {remover.isPending ? 'Removendo…' : 'Remover'}
          </Botao>
        </>
      }
    >
      <p className="conexoes__nota" id={porQue}>
        A senha sai do cofre e a conexão some daqui. Enquanto uma operação publicada usar esta
        conexão, o serviço recusa: arquive a operação antes.
      </p>
      {remover.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(remover.error, {
            generica: 'Não foi possível remover agora. Nada foi alterado; tente de novo.',
            porCodigo: {
              CONFLITO_DE_ESTADO:
                'Uma operação publicada ainda usa esta conexão. Arquive a operação e tente de novo.',
              ENGINE_INDISPONIVEL: SERVICO_FORA,
            },
          })}
        </Faixa>
      )}
    </Dialogo>
  );
}
